import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { fetchReport, handleError, fetch } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../../providers/ConfigProvider';

const OwnersNotificationsReport = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { config } = useConfig();
  
  // Load saved search parameters when returning to the page
  useEffect(() => {
    fetchNotifications(1);
  }, []);

  const fetchNotifications = async (pageNumber) => {
    setIsLoading(true);
    try {
      const response = await fetchReport('OWNERS_NOTIFICATIONS', pageNumber);
      setData(response);
      setPage(pageNumber);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePageChange = async (event, value) => {
    fetchNotifications(value);
  };

  const handleOwnerClick = (notification) => {
    if (notification.owner?.id) {
      navigate('/main/view-owner', { state: { id: notification.owner.id } });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusChip = (notification) => {
    if (notification.message_sended === 1) {
      return (
        <Chip 
          icon={<CheckCircleIcon />} 
          label={t('reports.sent')} 
          color="success" 
          size="small" 
        />
      );
    } else if (notification.error) {
      return (
        <Chip 
          icon={<ErrorIcon />} 
          label={notification.error === 'NO_EMAIL' ? t('reports.noEmail') : t('reports.error')} 
          color="error" 
          size="small" 
        />
      );
    } else {
      return (
        <Chip 
          label={t('agenda.pending')} 
          color="warning" 
          size="small" 
        />
      );
    }
  };

  const handleViewMessage = async (notificationId) => {
    setIsLoadingMessage(true);
    setMessageDialogOpen(true);
    setSelectedMessage(null);
    
    try {
      const response = await fetch('owners', { notification_id: notificationId });
      // Extract message and subject from the response
      const messageData = {
        subject: response?.data?.subject || t('reports.noSubject'),
        message: response?.data?.message || t('reports.noMessageAvailable')
      };
      setSelectedMessage(messageData);
    } catch (error) {
      handleError(error, snackbar);
      setMessageDialogOpen(false);
    } finally {
      setIsLoadingMessage(false);
    }
  };

  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setSelectedMessage(null);
  };

  return (
    <Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t('reports.notificationsTitle')} ({data.total_results || data.content.length})
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('reports.notificationType')}</TableCell>
                      <TableCell>{config?.vet?.owner_naming_es || t('reports.owner')}</TableCell>
                      <TableCell>{config?.vet?.pet_naming_es || t('reports.pet')}</TableCell>
                      <TableCell>{t('reports.dateShown')}</TableCell>
                      <TableCell>{t('common.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.content.map((notification) => (
                      <TableRow 
                        key={notification.id} 
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleOwnerClick(notification)}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <NotificationsIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                            <Typography variant="body2" fontWeight="medium">
                              {notification.caption || notification.notification_type}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {notification.owner?.name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {notification.pet?.name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(notification.shown_at || notification.created_at)}
                          </Typography>
                          {notification.created_at && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('reports.created')} {formatDate(notification.created_at)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
                            {getStatusChip(notification)}
                            <Chip
                              label={t('reports.viewMessage')}
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewMessage(notification.id);
                              }}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noNotifications')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('common.noData')}</Typography>
        </Box>
      )}

      {/* Dialog para mostrar el mensaje */}
      <Dialog
        open={messageDialogOpen}
        onClose={handleCloseMessageDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('reports.notificationMessage')}</DialogTitle>
        <DialogContent>
          {isLoadingMessage ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedMessage ? (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom color="primary">
                {selectedMessage.subject}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 2 }}>
                {selectedMessage.message}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('reports.couldNotLoadMessage')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMessageDialog} color="primary">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OwnersNotificationsReport; 